import { Form, Select, Tooltip } from 'antd';
import { useContext } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { BillingInterval } from 'app/typings/billingIntervals';
import { capitalizeFirstLetter } from 'app/utils/string';

import type { FinanceFormData } from '../type';

const { Option } = Select;

const BILLING_INTERVALS = [
  { value: BillingInterval.DAILY, label: 'Daily', isDisabled: true },
  { value: BillingInterval.WEEKLY, label: 'Weekly', isDisabled: false },
  { value: BillingInterval.MONTHLY, label: 'Monthly', isDisabled: false },
];

type InvoicingFrequencySectionProps = {
  isEditMode: boolean;
  hotelId: number;
};

export const InvoicingFrequencySection = ({
  isEditMode,
  hotelId,
}: InvoicingFrequencySectionProps) => {
  const { data: hotel } = useGetHotel(+hotelId);
  const { isSuperAdmin } = useContext(UserContext);

  const form = Form.useFormInstance<FinanceFormData>();
  const paymentMethod = Form.useWatch(['paymentMethod'], form);

  const issuingActivated = paymentMethod === 'vcc';

  const billingInterval =
    issuingActivated || !hotel?.billingInterval
      ? BillingInterval.MONTHLY
      : hotel.billingInterval;

  const isDisabled = !isSuperAdmin || issuingActivated;

  const tooltipTitle =
    isEditMode && isDisabled
      ? !isSuperAdmin
        ? 'Please contact a Super Admin to edit the payment frequency'
        : 'You cannot edit the billing interval for VCC-enabled hotels'
      : undefined;

  if (!isEditMode) {
    return (
      <FormLineLayout label="Billing interval">
        <span>{capitalizeFirstLetter(BillingInterval[billingInterval])}</span>
      </FormLineLayout>
    );
  }

  return (
    <FormLineLayout label="Billing interval">
      <Tooltip title={tooltipTitle}>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.paymentMethod !== cur.paymentMethod}
        >
          {({ getFieldValue }) => {
            const isVcc = getFieldValue('paymentMethod') === 'vcc';

            return (
              <Form.Item
                name="billingInterval"
                getValueProps={(value) => ({
                  value: isVcc ? BillingInterval.MONTHLY : value,
                })}
              >
                <Select
                  disabled={!isSuperAdmin || isVcc}
                  placeholder="Select billing interval"
                >
                  {BILLING_INTERVALS.map((interval) => (
                    <Option
                      key={interval.value}
                      value={interval.value}
                      disabled={interval.isDisabled}
                    >
                      {interval.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
      </Tooltip>
    </FormLineLayout>
  );
};
